import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  useTheme,
} from '@mui/material';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';
import { format } from 'date-fns';
import { CollectionTrend } from '../../../api/types/dashboard.types';

interface CollectionsChartProps {
  data?: CollectionTrend[];
  isLoading?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const CollectionsChart: React.FC<CollectionsChartProps> = ({
  data = [],
  isLoading = false,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={300} />
        </CardContent>
      </Card>
    );
  }

  const formattedData = data.map((item) => ({
    ...item,
    displayDate: format(new Date(item.date), 'MMM dd'),
  }));

  interface TooltipPayload {
    value: number;
    payload: CollectionTrend & { displayDate: string };
  }

  interface CustomTooltipProps {
    active?: boolean;
    payload?: TooltipPayload[];
  }

  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: theme.shadows[4],
          }}
        >
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {format(new Date(payload[0].payload.date), 'MMMM dd, yyyy')}
          </Typography>
          <Typography variant="body2" color="success.main">
            Collected: {formatCurrency(payload[0].value)}
          </Typography>
          <Typography variant="body2" color="primary.main">
            Target: {formatCurrency(payload[1].value)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Achievement:{' '}
            {((payload[0].value / payload[1].value) * 100).toFixed(1)}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const totalCollected = data.reduce((sum, item) => sum + item.amount, 0);
  const totalTarget = data.reduce((sum, item) => sum + item.target, 0);
  const achievementRate = totalTarget > 0 ? (totalCollected / totalTarget) * 100 : 0;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Collections Trend
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Daily collection vs target (Last 30 days)
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {achievementRate.toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Achievement Rate
            </Typography>
          </Box>
        </Box>

        {data.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 300,
            }}
          >
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={formattedData}>
              <defs>
                <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="displayDate"
                stroke={theme.palette.text.secondary}
                style={{ fontSize: 12 }}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                style={{ fontSize: 12 }}
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="line"
                formatter={(value) => (
                  <span style={{ fontSize: 14, color: theme.palette.text.primary }}>
                    {value}
                  </span>
                )}
              />
              <Area
                type="monotone"
                dataKey="amount"
                fill="url(#colorAmount)"
                stroke="none"
                name="Collected"
              />
              <Line
                type="monotone"
                dataKey="amount"
                stroke={theme.palette.success.main}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                name="Collected"
              />
              <Line
                type="monotone"
                dataKey="target"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Target"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight="bold" color="success.main">
              {formatCurrency(totalCollected)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Collected
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {formatCurrency(totalTarget)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Target
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CollectionsChart;
